import axios from "axios";

const baseURL = process.env.REACT_APP_BACKEND_URL;

// --- Auth & common fetch --------------------------------------------------

async function getAuthHeaders() {
  const token = localStorage.getItem("apiToken");
  if (!token) throw new Error("Not authenticated");
  return {
    Accept: "application/json",
    Authorization: `Token ${token}`,
  };
}

export async function fetchEstates(query) {
  if (query.length < 3) return [];
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${baseURL}/api/estates-minimal/?q=${encodeURIComponent(query)}`,
    { method: "GET", headers }
  );
  if (!res.ok) throw new Error(`Estate search failed: ${res.status}`);
  const { results } = await res.json();
  return results;
}

async function fetchArray(path) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${baseURL}${path}`, { headers });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(`Fetch ${path} failed: ${res.status}`);
  }
  return Array.isArray(json) ? json : json.results || [];
}

// --- Read helpers ---------------------------------------------------------

export function fetchMeterBrands() {
  return fetchArray("/api/meter-brands/");
}

export function fetchQnoms() {
  return fetchArray("/api/qnoms/");
}

export function fetchDiameters() {
  return fetchArray("/api/diameters/");
}

export function fetchPriceLists() {
  return fetchArray("/api/price-lists/");
}

// --- Create helpers -------------------------------------------------------

export async function createMeterBrand(name) {
  const headers = await getAuthHeaders();
  const res = await axios.post(
    `${baseURL}/api/meter-brands/`,
    { name },
    { headers }
  );
  return res.data;
}

export async function createQnom(q_nom) {
  const headers = await getAuthHeaders();
  const res = await axios.post(`${baseURL}/api/qnoms/`, { q_nom }, { headers });
  return res.data;
}

export async function createDiameter(diameter) {
  const headers = await getAuthHeaders();
  const res = await axios.post(
    `${baseURL}/api/diameters/`,
    { diameter },
    { headers }
  );
  return res.data;
}

// --- Delete helpers -------------------------------------------------------

export async function deleteMeterBrand(id) {
  const headers = await getAuthHeaders();
  await axios.delete(`${baseURL}/api/meter-brands/${id}/`, { headers });
}

export async function deleteQnom(id) {
  const headers = await getAuthHeaders();
  await axios.delete(`${baseURL}/api/qnoms/${id}/`, { headers });
}

export async function deleteDiameter(id) {
  const headers = await getAuthHeaders();
  await axios.delete(`${baseURL}/api/diameters/${id}/`, { headers });
}

// --- Read multiple meter reports -----------------------------------------

export function fetchMeterReports() {
  return fetchArray("/api/meter-reports/");
}

export function fetchMeterReplacementDrafts() {
  return fetchArray("/api/meter-replacement-drafts/");
}

// --- Delete a meter report by number -------------------------------------

export async function deleteMeterReport(number) {
  const headers = await getAuthHeaders();
  await axios.delete(`${baseURL}/api/meter-reports/${number}/`, { headers });
}

export async function deleteMeterReplacementDraft(id) {
  const headers = await getAuthHeaders();
  await axios.delete(`${baseURL}/api/meter-replacement-drafts/${id}/`, {
    headers,
  });
}

// --- Create / Update a meter report --------------------------------------

export async function createMeterReport(report) {
  const headers = await getAuthHeaders();
  const res = await axios.post(`${baseURL}/api/meter-reports/`, report, {
    headers,
  });
  return res.data;
}

export async function createMeterReplacementDraft(payload) {
  const headers = await getAuthHeaders();
  const res = await axios.post(
    `${baseURL}/api/meter-replacement-drafts/`,
    { payload },
    { headers }
  );
  return res.data;
}

export async function searchMeterReports(meterNumber) {
  const headers = await getAuthHeaders();
  const res = await axios.get(
    `${baseURL}/api/meter-reports/?meter_number=${encodeURIComponent(
      meterNumber
    )}`,
    { headers }
  );
  // Django Rest Framework paginates list results under `results`
  return Array.isArray(res.data.results) ? res.data.results : [];
}

export async function searchMeters(systemNumber, activeOnly = false) {
  const headers = await getAuthHeaders();
  let url = `${baseURL}/api/meters/?system_number=${encodeURIComponent(systemNumber)}`;
  
  // For remove operations, only search active meters
  if (activeOnly) {
    url += '&status=ACTIVE';
  }
  
  const res = await axios.get(url, { headers });
  // Django Rest Framework paginates list results under `results`
  return Array.isArray(res.data.results) ? res.data.results : [];
}

export async function fetchMetersByEstate(estateId) {
  const headers = await getAuthHeaders();
  const res = await axios.get(
    `${baseURL}/api/meters/?estate=${encodeURIComponent(estateId)}`,
    { headers }
  );
  // Django Rest Framework paginates list results under `results`
  return Array.isArray(res.data.results) ? res.data.results : [];
}

export async function updateMeterReport(number, report) {
  const headers = await getAuthHeaders();
  const res = await axios.patch(
    `${baseURL}/api/meter-reports/${number}/`,
    report,
    { headers }
  );
  return res.data;
}

export async function updateMeterReplacementDraft(id, payload) {
  const headers = await getAuthHeaders();
  const res = await axios.patch(
    `${baseURL}/api/meter-replacement-drafts/${id}/`,
    { payload },
    { headers }
  );
  return res.data;
}

export async function fetchMeterReplacementDraft(id) {
  const headers = await getAuthHeaders();
  const res = await axios.get(
    `${baseURL}/api/meter-replacement-drafts/${id}/`,
    { headers }
  );
  return res.data;
}

// --- New: Read a single meter report by number ---------------------------

export async function fetchMeterReport(meterNumber) {
  const headers = await getAuthHeaders();
  const res = await axios.get(`${baseURL}/api/meter-reports/${meterNumber}/`, {
    headers,
  });
  return res.data;
}

// --- New: Read work types for removal/installation ------------------------

export async function fetchTypes() {
  const headers = await getAuthHeaders();
  const res = await axios.get(`${baseURL}/api/types/`, { headers });
  return res.data;
}

export async function uploadMeterReportImage(reportId, fileBlob, filename) {
  const headers = await getAuthHeaders();
  const form = new FormData();
  form.append("report", reportId);
  // give it a real filename so DRF can infer the extension
  form.append("image", fileBlob, filename);

  // don't set content-type here — let the browser do it:
  const res = await axios.post(`${baseURL}/api/meter-report-images/`, form, {
    headers,
  });
  return res.data;
}

export async function fetchMeterReportImages(reportNumber) {
  const report = await fetchMeterReport(reportNumber);
  const headers = await getAuthHeaders();

  const blobs = await Promise.all(
    (report.images || []).map((img) =>
      fetch(img.raw_url, { headers }).then((res) => {
        if (!res.ok) throw new Error("Image fetch failed");
        return res.blob();
      })
    )
  );

  return blobs.map((blob) => URL.createObjectURL(blob));
}

// Get image URLs via backend proxy
export function getImageProxyUrl(imageId) {
  const token = localStorage.getItem("apiToken");
  return `${baseURL}/api/meter-report-images/${imageId}/proxy/?token=${token}`;
}

// --- Meter replacement ----------------------------------------------------

export async function replaceMeter(oldMeterId, newMeterId, finalReading, oldMeterReportId = null) {
  const headers = await getAuthHeaders();
  const res = await axios.post(
    `${baseURL}/api/meter-replacements/`,
    {
      old_meter_id: oldMeterId,
      new_meter_id: newMeterId,
      final_reading: finalReading,
      ...(oldMeterReportId ? { old_meter_report_id: oldMeterReportId } : {}),
    },
    { headers }
  );
  return res.data;
}

// --- Meter removal --------------------------------------------------------

export async function removeMeter(meterId, meterReportId, finalReading) {
  const headers = await getAuthHeaders();
  // Ensure reading is a string with dot as decimal separator
  const readingStr = typeof finalReading === "number" ? finalReading.toString() : finalReading;
  const res = await axios.post(
    `${baseURL}/api/meter-removals/`,
    {
      meter_id: meterId,
      meter_report_id: meterReportId,
      final_reading: readingStr,
    },
    { headers }
  );
  return res.data;
}

// --- Meter addition -------------------------------------------------------

export async function addMeter(
  meterReportId,
  initialReading,
  priceListId = null
) {
  const headers = await getAuthHeaders();
  const payload = {
    meter_report_id: meterReportId,
    initial_reading: initialReading,
  };

  if (priceListId) {
    payload.price_list_id = priceListId;
  }

  const res = await axios.post(`${baseURL}/api/meter-additions/`, payload, {
    headers,
  });
  return res.data;
}

// Check if an ACTIVE meter with specific number already exists on an estate
export async function checkMeterExists(meterNumber, estateId) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  
  if (meterNumber) {
    params.append('system_number', meterNumber);
  }
  if (estateId) {
    params.append('estate', estateId);
  }

  const res = await axios.get(`${baseURL}/api/meters/?${params.toString()}`, {
    headers,
  });
  
  // Return true if any ACTIVE meters found with this number on this estate
  const existingMeters = res.data.results || res.data;
  return existingMeters.some(meter => 
    meter.system_number === String(meterNumber) && 
    meter.estate === estateId &&
    meter.status === 'ACTIVE'
  );
}
